import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

type StepState = "waiting" | "processing" | "done" | "error";

const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 60; // 2 minutes at 2s interval

const PaymentCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const bookingId = searchParams.get("bookingId");
  const userIdFromUrl = searchParams.get("userId");
  const { user } = useAuth();
  const bookingUserId = userIdFromUrl || user?.uid;

  const [step, setStep] = useState<StepState>("waiting");
  const [message, setMessage] = useState<string>(
    "Processing your payment. Please wait..."
  );
  const [error, setError] = useState<string | null>(null);

  // Use ref to prevent duplicate processing (React Strict Mode or re-renders)
  const hasProcessedRef = useRef(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!bookingId || !bookingUserId) {
      setStep("error");
      setError("Missing booking or user information.");
      return;
    }

    // Prevent duplicate processing
    if (hasProcessedRef.current) {
      console.log(
        "[PaymentCallback] Already processing, skipping duplicate call"
      );
      return;
    }

    hasProcessedRef.current = true;
    let pollCount = 0;

    // Helper function to format user-friendly error messages
    const formatErrorMessage = (
      errorDetails: string,
      refundRequired?: boolean
    ): string => {
      const errorLower = errorDetails.toLowerCase();

      // Handle specific error cases
      if (
        errorLower.includes("flights present are not available") ||
        errorLower.includes("not available/confirmed") ||
        errorLower.includes("flights not available")
      ) {
        if (refundRequired) {
          return (
            "Unfortunately, the selected flights are no longer available. " +
            "Your payment has been captured, but we cannot complete the booking. " +
            "Your payment will be refunded automatically within 5-7 business days. " +
            "Please contact support if you have any questions."
          );
        }
        return (
          "Unfortunately, the selected flights are no longer available. " +
          "Please try selecting different flights."
        );
      }

      if (errorLower.includes("failed to generate pnr")) {
        return (
          "We encountered an issue while creating your booking reservation. " +
          (refundRequired
            ? "Your payment will be refunded automatically. Please contact support."
            : "Please try again or contact support.")
        );
      }

      if (errorLower.includes("failed to issue ticket")) {
        return (
          "We encountered an issue while issuing your ticket. " +
          (refundRequired
            ? "Your payment will be refunded automatically. Please contact support for assistance."
            : "Please contact support for assistance.")
        );
      }

      // Generic error message
      return (
        errorDetails ||
        "An unexpected error occurred while processing your booking. " +
          (refundRequired
            ? "Your payment will be refunded if it was captured. Please contact support."
            : "Please contact support.")
      );
    };

    // True once the flow has reached an end state — ticketed, failed, or out
    // of time. Without it, a first poll that finishes immediately (the booking
    // the webhook already completed) would still have an interval started
    // underneath it, firing one more request against a screen that is already
    // navigating away.
    let settled = false;

    const pollBookingStatus = async () => {
      try {
        const statusRes = await fetch(
          `${process.env.REACT_APP_API_URL}/booking-status/${bookingId}?userId=${bookingUserId}`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
          }
        );

        if (!statusRes.ok) {
          throw new Error(`Status check failed: ${statusRes.status}`);
        }

        const statusData = await statusRes.json();

        if (statusData.ticketingStatus === "success" && statusData.pnr) {
          setStep("done");
          setMessage("Ticket issued successfully! Redirecting...");
          settled = true;
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          setTimeout(() => {
            navigate(
              `/booking-confirmed?pnr=${statusData.pnr}&bookingId=${bookingId}`
            );
          }, 2000);
          return;
        }

        if (
          statusData.paymentStatus === "FAILED" ||
          statusData.ticketingStatus === "failed"
        ) {
          setStep("error");

          // Format error message if available
          const errorMessage = statusData.ticketingError
            ? formatErrorMessage(
                statusData.ticketingError,
                statusData.paymentStatus === "paid" ||
                  statusData.paymentStatus === "CAPTURED"
              )
            : "Booking processing failed. Please contact support.";

          setError(errorMessage);
          settled = true;
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          return;
        }

        pollCount += 1;
        if (pollCount >= MAX_POLLS) {
          setStep("error");
          setError("Payment processing timeout. Please contact support.");
          settled = true;
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
        }
      } catch (err) {
        console.error("Booking status poll error:", err);
        pollCount += 1;
        if (pollCount >= MAX_POLLS) {
          setStep("error");
          setError("Unable to check payment status. Please contact support.");
          settled = true;
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
        }
      }
    };

    // Straight to polling.
    //
    // This used to POST /payments/start-processing first, and only fall back to
    // polling. That endpoint does not exist on the server — and could not be
    // reached even if it did, because nginx does not proxy /payments at all. So
    // the call came back as nginx's HTML 405, res.json() threw parsing it, and
    // the catch reported "Failed to connect to the server" to a customer whose
    // card had just been charged successfully. Polling never started, so the
    // screen never recovered even though the webhook was finishing the booking
    // normally in the background.
    //
    // Nothing is lost by dropping it. The N-Genius webhook is what creates the
    // PNR and issues the ticket on CAPTURED, and the first poll answers at once
    // for a booking it has already finished — which is what that endpoint's
    // "already_processed" reply existed for. Building a second path that issues
    // tickets would put two of them in a race to create one PNR for one
    // payment, which is a worse problem than a slightly later first answer.
    const beginPolling = async () => {
      setStep("processing");
      setMessage("Payment captured! Finalizing your booking...");

      await pollBookingStatus();

      // Only keep asking if that first answer was not already the last one.
      if (!settled) {
        pollingIntervalRef.current = setInterval(
          pollBookingStatus,
          POLL_INTERVAL_MS
        );
      }
    };

    beginPolling();

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
    // Only depend on bookingId and userId - navigate is stable and shouldn't trigger re-runs
  }, [bookingId, bookingUserId]);

  const renderContent = () => {
    switch (step) {
      case "waiting":
      case "processing":
        return (
          <div className="flex flex-col items-center">
            <span className="text-primary text-lg font-sora mb-2">
              Payment successful!
            </span>
            <span className="text-heading text-base text-center">
              {message}
            </span>
          </div>
        );
      case "done":
        return (
          <div className="flex flex-col items-center">
            <span className="text-primary text-lg font-sora mb-2">
              Ticket issued successfully!
            </span>
            <span className="text-heading text-base text-center">
              Redirecting to your booking...
            </span>
          </div>
        );
      case "error":
      default:
        return (
          <div className="flex flex-col items-center">
            <span className="text-red-500 text-lg font-sora mb-2">
              Booking error
            </span>
            <span className="text-red-500 text-base text-center">{error}</span>
            <button
              className="mt-4 bg-primary text-white px-6 py-2 rounded-lg hover:bg-blue-900 transition"
              onClick={() => navigate("/results")}
            >
              Return to Search
            </button>
          </div>
        );
    }
  };

  return (
    <div className="flex flex-col items-center justify-center h-screen font-sans bg-card">
      {renderContent()}
    </div>
  );
};

export default PaymentCallback;
